'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@module/mdrt-org/shared/components/button/button';
import { Dialog } from '@module/mdrt-org/shared/components/dialog/dialog';
import { FieldController } from '@module/mdrt-org/shared/components/field-controller/field-controller';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { SelectField } from '@module/mdrt-org/shared/components/select-field/select-field';
import { MAX_BIO_LENGTH, SOCIAL_MEDIA_LINK_LENGTH } from '@module/mdrt-org/shared/utils/constants/inputs-constants';
import { deleteProfilePicture } from '@module/mdrt-org/shared/utils/data/delete-profile-picture';
import { AccountSettingsFormStep } from '@module/mdrt-org/shared/utils/enums/account-settings-form-step';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import { DevelopedFeatures } from '@module/mdrt-org/shared/utils/feature-flags/constants';
import { useFeatureFlags } from '@module/mdrt-org/shared/utils/feature-flags/use-feature-flags';
import { allowedCharactersTransform } from '@module/mdrt-org/shared/utils/helpers/allowed-characters-transform';
import { getProfileDisplayName } from '@module/mdrt-org/shared/utils/helpers/get-profile-display-name';
import { useAddDefaultErrorToast } from '@module/mdrt-org/shared/utils/hooks/use-add-default-error-toast';
import { useConfirmNavigation } from '@module/mdrt-org/shared/utils/hooks/use-confirm-navigation';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { useProfilePictureUploadMutation } from '@module/mdrt-org/shared/utils/hooks/use-profile-picture-upload-mutation';
import { ProfilePhotoContainer } from '@shared/profile-dashboard-sections/profile-dashboard-hero-section/components/profile-photo-container/profile-photo-container';
import { FormInput } from '@shared/ui-components/form-input/form-input';
import { FormTextarea } from '@shared/ui-components/form-textarea/form-textarea';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { ProfilePhotoSize } from '@shared/ui-components/profile-photo/profile-photo';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { GTM_ACTION_CLICK, GTM_EVENT_ACCOUNT_PROFILE_UPDATE, GTM_EVENT_UPLOAD_PHOTO, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { ButtonType } from '@shared/utils/enums/button-type';
import { getTimezoneOptions } from '@shared/utils/helpers/get-timezone-options';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { SocialPlatform, validateSocialLink } from '@shared/utils/helpers/social-link-validators';
import { type SelectOption } from '@shared/utils/types/select-option';
import { type ServerActionResponse } from '@shared/utils/types/server-action';
import { saveAccountSettingsProfileStepAction } from '../../actions/save-account-settings-profile-step-action';
import { useAccountSettingsPageContext } from '../../providers/account-settings-page-context';
import { type ProfileFormData } from '../../utils/types/form-data';
import { type AccountSettingsProfileContent } from './get-account-settings-profile-content';
import { MembershipPrintouts } from './membership-printouts/membership-printouts';
import styles from './profile-form.module.scss';
type ProfileFormProps = {
  cmsContent: AccountSettingsProfileContent;
  isPrintoutsVisible: boolean;
  membershipCertificateEndpointUrl: string;
  tabUrl: string;
};
const transformInputLineBreaks = (inputString: null | string) => inputString && inputString.replaceAll('\r\n', '\n');
export const ProfileForm = t0 => {
  const $ = _c(142);
  const {
    cmsContent,
    isPrintoutsVisible,
    membershipCertificateEndpointUrl,
    tabUrl
  } = t0;
  const {
    countriesData,
    customerProfileContextData,
    profilePictureContextData,
    resetCustomerProfileContextData,
    resetProfilePictureContextData,
    setCustomerProfileContextData
  } = useAccountSettingsPageContext();
  const {
    personId
  } = useUserContext();
  const addErrorToast = useAddDefaultErrorToast();
  const {
    getIsFeatureEnabled
  } = useFeatureFlags();
  let t1;
  if ($[0] !== getIsFeatureEnabled) {
    t1 = getIsFeatureEnabled(DevelopedFeatures.TimeZoneSelectProfile);
    $[0] = getIsFeatureEnabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isTimeZoneSelectEnabled = t1;
  const {
    cancel: abortOngoingRequests,
    mutate: uploadProfilePicture
  } = useProfilePictureUploadMutation();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getTimezoneOptions();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const timezoneOptions = t2;
  let t3;
  if ($[3] !== customerProfileContextData) {
    t3 = getProfileDisplayName(customerProfileContextData);
    $[3] = customerProfileContextData;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const profileDisplayName = t3;
  let t4;
  if ($[5] !== countriesData || $[6] !== customerProfileContextData.countryId) {
    let t5;
    if ($[8] !== customerProfileContextData.countryId) {
      t5 = country => country.countryId === customerProfileContextData.countryId;
      $[8] = customerProfileContextData.countryId;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = countriesData.find(t5);
    $[5] = countriesData;
    $[6] = customerProfileContextData.countryId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const defaultCountry = t4;
  let t5;
  if ($[10] !== uploadProfilePicture) {
    t5 = async formData => {
      if (formData instanceof FormData) {
        const file = formData.get("file");
        if (file && file instanceof File) {
          sendGtmEvent({
            action: GTM_ACTION_CLICK,
            event: GTM_EVENT_USER_INTERACTIONS,
            eventName: GTM_EVENT_UPLOAD_PHOTO,
            label: file?.name || ""
          });
        }
      }
      return await uploadProfilePicture(formData);
    };
    $[10] = uploadProfilePicture;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleSubmitProfilePicture = t5;
  let t6;
  if ($[12] !== customerProfileContextData.bio) {
    t6 = transformInputLineBreaks(customerProfileContextData.bio) || "";
    $[12] = customerProfileContextData.bio;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== defaultCountry) {
    t7 = defaultCountry ? {
      label: defaultCountry.name,
      value: defaultCountry.countryId
    } : undefined;
    $[14] = defaultCountry;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = profileDisplayName || "";
  const t9 = customerProfileContextData.facebook || "";
  const t10 = customerProfileContextData.instagram || "";
  const t11 = customerProfileContextData.linkedin || "";
  let t12;
  if ($[16] !== customerProfileContextData.timeZoneId) {
    t12 = timezoneOptions?.find(timezoneOption => timezoneOption?.value === customerProfileContextData?.timeZoneId);
    $[16] = customerProfileContextData.timeZoneId;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = customerProfileContextData.twitter || "";
  let t14;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t13 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t14 = {
      bio: t6,
      country: t7,
      displayName: t8,
      facebook: t9,
      instagram: t10,
      linkedin: t11,
      timeZoneId: t12,
      twitter: t13
    };
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      keepValues: true
    };
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t14) {
    t16 = {
      ...formBaseConfig,
      defaultValues: t14,
      resetOptions: t15
    };
    $[28] = t14;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const formMethods = useForm(t16);
  const {
    formState: t17,
    reset
  } = formMethods;
  const {
    isDirty
  } = t17;
  let t18;
  if ($[30] !== countriesData) {
    t18 = countriesData?.map(_temp);
    $[30] = countriesData;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const countriesOptions = t18;
  let t19;
  if ($[32] !== addErrorToast || $[33] !== resetCustomerProfileContextData || $[34] !== resetProfilePictureContextData) {
    t19 = result => {
      if (result.status !== 200 && "message" in result) {
        resetCustomerProfileContextData();
        resetProfilePictureContextData();
        addErrorToast();
      }
    };
    $[32] = addErrorToast;
    $[33] = resetCustomerProfileContextData;
    $[34] = resetProfilePictureContextData;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const onFailure = t19;
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = [AccountSettingsMutationKey.PATCH_ACCOUNT_SETTINGS];
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== formMethods || $[38] !== reset) {
    t21 = () => reset({
      ...formMethods.getValues()
    });
    $[37] = formMethods;
    $[38] = reset;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== onFailure || $[41] !== t21) {
    t22 = {
      mutationFunction: saveAccountSettingsProfileStepAction,
      mutationKey: t20,
      onFailure,
      onSuccessHandler: t21
    };
    $[40] = onFailure;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const {
    isMutationPending,
    mutate
  } = useMutationHandler(t22);
  let t23;
  if ($[43] !== customerProfileContextData || $[44] !== mutate || $[45] !== personId || $[46] !== setCustomerProfileContextData) {
    t23 = async data => {
      const formData_0 = new FormData();
      setCustomerProfileContextData({
        ...customerProfileContextData,
        bio: data.bio || "",
        countryId: Number(data.country?.value) || null,
        facebook: data.facebook || "",
        instagram: data.instagram || "",
        linkedin: data.linkedin || "",
        timeZoneId: data.timeZoneId?.value?.toString() || "",
        twitter: data.twitter || ""
      });
      formData_0.append("accountSettingStep", AccountSettingsFormStep.Profile);
      formData_0.append("profileData['bio']", data.bio || "");
      formData_0.append("profileData['countryId']", data.country ? data.country.value.toString() : "");
      formData_0.append("profileData['timeZoneId']", data.timeZoneId?.value?.toString() || "");
      formData_0.append("profileData['linkedin']", data.linkedin || "");
      formData_0.append("profileData['facebook']", data.facebook || "");
      formData_0.append("profileData['instagram']", data.instagram || "");
      formData_0.append("profileData['twitter']", data.twitter || "");
      mutate({
        formData: formData_0,
        personId
      });
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_ACCOUNT_PROFILE_UPDATE,
        label: "Profile"
      });
    };
    $[43] = customerProfileContextData;
    $[44] = mutate;
    $[45] = personId;
    $[46] = setCustomerProfileContextData;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  const handleSubmit = t23;
  useConfirmNavigation(isDirty, tabUrl);
  let t24;
  if ($[48] !== cmsContent.tabTitle) {
    t24 = <h1 className={styles["profile-form__heading"]}>{cmsContent.tabTitle}</h1>;
    $[48] = cmsContent.tabTitle;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== personId) {
    t25 = async () => await deleteProfilePicture({
      personId
    });
    $[50] = personId;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const t26 = profilePictureContextData ?? null;
  let t27;
  if ($[52] !== abortOngoingRequests || $[53] !== cmsContent.profilePicture || $[54] !== handleSubmitProfilePicture || $[55] !== t25 || $[56] !== t26) {
    t27 = <ProfilePhotoContainer abortOngoingRequests={abortOngoingRequests} cmsContent={cmsContent.profilePicture} dialogComponent={Dialog} handleProfilePictureDelete={t25} handleSubmitProfilePicture={handleSubmitProfilePicture} initialProfilePicture={t26} size={ProfilePhotoSize.XSmall} />;
    $[52] = abortOngoingRequests;
    $[53] = cmsContent.profilePicture;
    $[54] = handleSubmitProfilePicture;
    $[55] = t25;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <FormInput disabled={true} />;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== cmsContent.displayName) {
    t29 = <FieldController label={cmsContent.displayName} name="displayName">{t28}</FieldController>;
    $[59] = cmsContent.displayName;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== cmsContent.requiredLabel) {
    t30 = {
      required: cmsContent.requiredLabel
    };
    $[61] = cmsContent.requiredLabel;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== cmsContent.country || $[64] !== countriesOptions || $[65] !== t30) {
    t31 = <SelectField id="country" isSearchable={true} label={cmsContent.country} name="country" options={countriesOptions} rules={t30} />;
    $[63] = cmsContent.country;
    $[64] = countriesOptions;
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== cmsContent.requiredLabel || $[68] !== cmsContent.timeZone || $[69] !== isTimeZoneSelectEnabled) {
    t32 = isTimeZoneSelectEnabled && <SelectField id="timeZoneId" isSearchable={true} label={cmsContent.timeZone} name="timeZoneId" options={timezoneOptions} rules={{
      required: cmsContent.requiredLabel
    }} />;
    $[67] = cmsContent.requiredLabel;
    $[68] = cmsContent.timeZone;
    $[69] = isTimeZoneSelectEnabled;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <FormTextarea />;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== cmsContent.bio) {
    t34 = <FieldController displayMaxLengthCounter={true} label={cmsContent.bio} maxLength={MAX_BIO_LENGTH} name="bio" transform={allowedCharactersTransform}>{t33}</FieldController>;
    $[72] = cmsContent.bio;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== cmsContent || $[75] !== customerProfileContextData || $[76] !== isPrintoutsVisible || $[77] !== membershipCertificateEndpointUrl) {
    t35 = <MembershipPrintouts cmsContent={cmsContent} customerProfile={customerProfileContextData} isPrintoutsVisible={isPrintoutsVisible} membershipCertificateEndpointUrl={membershipCertificateEndpointUrl} />;
    $[74] = cmsContent;
    $[75] = customerProfileContextData;
    $[76] = isPrintoutsVisible;
    $[77] = membershipCertificateEndpointUrl;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <hr className={styles["profile-form__separator"]} />;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== cmsContent.socialLinks.title) {
    t37 = <h2 className={styles["profile-form__heading"]}>{cmsContent.socialLinks.title}</h2>;
    $[80] = cmsContent.socialLinks.title;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== cmsContent.socialLinks.shareSocialLinks) {
    t38 = <p className={styles["profile-form__sub-heading"]}>{cmsContent.socialLinks.shareSocialLinks}</p>;
    $[82] = cmsContent.socialLinks.shareSocialLinks;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  const t39 = cmsContent.socialLinks.linkedIn;
  let t40;
  if ($[84] !== cmsContent.socialLinks.invalidLinkMessage) {
    t40 = validateSocialLink(SocialPlatform.LinkedIn, cmsContent.socialLinks.invalidLinkMessage);
    $[84] = cmsContent.socialLinks.invalidLinkMessage;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  let t41;
  if ($[86] !== t40) {
    t41 = {
      validate: t40
    };
    $[86] = t40;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  let t42;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <FormInput />;
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] !== cmsContent.socialLinks.linkedIn || $[90] !== t41) {
    t43 = <FieldController label={t39} maxLength={SOCIAL_MEDIA_LINK_LENGTH} name="linkedin" rules={t41}>{t42}</FieldController>;
    $[89] = cmsContent.socialLinks.linkedIn;
    $[90] = t41;
    $[91] = t43;
  } else {
    t43 = $[91];
  }
  const t44 = cmsContent.socialLinks.facebook;
  let t45;
  if ($[92] !== cmsContent.socialLinks.invalidLinkMessage) {
    t45 = validateSocialLink(SocialPlatform.Facebook, cmsContent.socialLinks.invalidLinkMessage);
    $[92] = cmsContent.socialLinks.invalidLinkMessage;
    $[93] = t45;
  } else {
    t45 = $[93];
  }
  let t46;
  if ($[94] !== t45) {
    t46 = {
      validate: t45
    };
    $[94] = t45;
    $[95] = t46;
  } else {
    t46 = $[95];
  }
  let t47;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <FormInput />;
    $[96] = t47;
  } else {
    t47 = $[96];
  }
  let t48;
  if ($[97] !== cmsContent.socialLinks.facebook || $[98] !== t46) {
    t48 = <FieldController label={t44} maxLength={SOCIAL_MEDIA_LINK_LENGTH} name="facebook" rules={t46}>{t47}</FieldController>;
    $[97] = cmsContent.socialLinks.facebook;
    $[98] = t46;
    $[99] = t48;
  } else {
    t48 = $[99];
  }
  const t49 = cmsContent.socialLinks.instagram;
  let t50;
  if ($[100] !== cmsContent.socialLinks.invalidLinkMessage) {
    t50 = validateSocialLink(SocialPlatform.Instagram, cmsContent.socialLinks.invalidLinkMessage);
    $[100] = cmsContent.socialLinks.invalidLinkMessage;
    $[101] = t50;
  } else {
    t50 = $[101];
  }
  let t51;
  if ($[102] !== t50) {
    t51 = {
      validate: t50
    };
    $[102] = t50;
    $[103] = t51;
  } else {
    t51 = $[103];
  }
  let t52;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <FormInput />;
    $[104] = t52;
  } else {
    t52 = $[104];
  }
  let t53;
  if ($[105] !== cmsContent.socialLinks.instagram || $[106] !== t51) {
    t53 = <FieldController label={t49} maxLength={SOCIAL_MEDIA_LINK_LENGTH} name="instagram" rules={t51}>{t52}</FieldController>;
    $[105] = cmsContent.socialLinks.instagram;
    $[106] = t51;
    $[107] = t53;
  } else {
    t53 = $[107];
  }
  const t54 = cmsContent.socialLinks.twitter;
  let t55;
  if ($[108] !== cmsContent.socialLinks.invalidLinkMessage) {
    t55 = validateSocialLink(SocialPlatform.Twitter, cmsContent.socialLinks.invalidLinkMessage);
    $[108] = cmsContent.socialLinks.invalidLinkMessage;
    $[109] = t55;
  } else {
    t55 = $[109];
  }
  let t56;
  if ($[110] !== t55) {
    t56 = {
      validate: t55
    };
    $[110] = t55;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <FormInput />;
    $[112] = t57;
  } else {
    t57 = $[112];
  }
  let t58;
  if ($[113] !== cmsContent.socialLinks.twitter || $[114] !== t56) {
    t58 = <FieldController label={t54} maxLength={SOCIAL_MEDIA_LINK_LENGTH} name="twitter" rules={t56}>{t57}</FieldController>;
    $[113] = cmsContent.socialLinks.twitter;
    $[114] = t56;
    $[115] = t58;
  } else {
    t58 = $[115];
  }
  let t59;
  if ($[116] !== t43 || $[117] !== t48 || $[118] !== t53 || $[119] !== t58) {
    t59 = <div className={styles["profile-form__social-links-fields"]}>{t43}{t48}{t53}{t58}</div>;
    $[116] = t43;
    $[117] = t48;
    $[118] = t53;
    $[119] = t58;
    $[120] = t59;
  } else {
    t59 = $[120];
  }
  let t60;
  if ($[121] !== t37 || $[122] !== t38 || $[123] !== t59) {
    t60 = <div>{t37}{t38}{t59}</div>;
    $[121] = t37;
    $[122] = t38;
    $[123] = t59;
    $[124] = t60;
  } else {
    t60 = $[124];
  }
  const t61 = !isDirty || isMutationPending;
  let t62;
  if ($[125] !== cmsContent.saveChanges || $[126] !== t61) {
    t62 = <div className={styles["profile-form__footer"]}><Button disabled={t61} type={ButtonType.SUBMIT}>{cmsContent.saveChanges}</Button></div>;
    $[125] = cmsContent.saveChanges;
    $[126] = t61;
    $[127] = t62;
  } else {
    t62 = $[127];
  }
  let t63;
  if ($[128] !== formMethods || $[129] !== handleSubmit || $[130] !== t29 || $[131] !== t31 || $[132] !== t32 || $[133] !== t34 || $[134] !== t35 || $[135] !== t60 || $[136] !== t62) {
    t63 = <FormWrapper className={styles["profile-form"]} dataTestId="profile-form" formMethods={formMethods} onSubmit={handleSubmit}>{t29}{t31}{t32}{t34}{t35}{t36}{t60}{t62}</FormWrapper>;
    $[128] = formMethods;
    $[129] = handleSubmit;
    $[130] = t29;
    $[131] = t31;
    $[132] = t32;
    $[133] = t34;
    $[134] = t35;
    $[135] = t60;
    $[136] = t62;
    $[137] = t63;
  } else {
    t63 = $[137];
  }
  let t64;
  if ($[138] !== t24 || $[139] !== t27 || $[140] !== t63) {
    t64 = <div className={styles["profile-form"]}>{t24}{t27}{t63}</div>;
    $[138] = t24;
    $[139] = t27;
    $[140] = t63;
    $[141] = t64;
  } else {
    t64 = $[141];
  }
  return t64;
};
function _temp(country_0) {
  return {
    label: country_0.name,
    value: `${country_0.countryId}`
  };
}